import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { Button, LoadingModal, Stack, Text } from '@adc/parallax-component-library';
import { ApiStatus, ErrorCode } from 'Constants';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAccount, useApiError } from 'Hooks';
import { FormContainer, InputPassword, PasswordInputValidator } from 'Components/utility';
import i18n from 'Utilities/i18n';
import { resetPass } from 'Services/auth';
import { Container } from './helpers/';
import { ResetFailure, ResetSuccess } from './states';
const mapStateToProps = ({ config: { passwordRules }, nav: { query: { email, token, product, version, country }, }, }) => {
    return {
        email,
        token,
        product,
        version,
        country,
        passwordRules,
    };
};
const initialValuesFormik = {
    newPassword: '',
    repeatNewPassword: '',
};
const connector = connect(mapStateToProps);
const ResetPassword = ({ token, email, product, version, country, passwordRules, }) => {
    const [loading, setLoading] = useState(false);
    const [flowState, setFlowState] = useState('form');
    const [meetsPasswordRequirements, setMeetsPasswordRequirements] = useState(false);
    const { showApiErrorModal, showNetworkErrorModal } = useApiError();
    const { showPasswordValidationError } = useAccount();
    const validationSchema = Yup.object().shape({
        repeatNewPassword: Yup.string()
            .required(i18n.t('ResetPassword.content.resetPasswordForm.formField.repeatNewPassword.errors.passwordMustMatch'))
            .oneOf([Yup.ref('newPassword')], i18n.t('ResetPassword.content.resetPasswordForm.formField.repeatNewPassword.errors.passwordMustMatch')),
    });
    const { handleSubmit, handleBlur, setValues, handleChange, isValid, errors, touched, values, setTouched, } = useFormik({
        initialValues: initialValuesFormik,
        onSubmit: onSubmitForm,
        validationSchema,
        validateOnBlur: true,
    });
    const handleApiRequestError = useCallback((error) => {
        switch (error.status) {
            case ApiStatus.BAD_REQUEST: {
                setValues(initialValuesFormik);
                setTouched({});
                const errors = error.response?.data.errors;
                const code = error.response?.data.code;
                if (code === ApiStatus.PASSWORD_VALIDATION) {
                    showPasswordValidationError(passwordRules, errors, country);
                    return;
                }
                showApiErrorModal();
                break;
            }
            case ApiStatus.UNAUTHORIZED: {
                setFlowState('failure');
                break;
            }
            default:
                error.code === ErrorCode.NETWORK_ERROR ? showNetworkErrorModal() : showApiErrorModal();
        }
    }, [
        showNetworkErrorModal,
        showApiErrorModal,
        setValues,
        setTouched,
        showPasswordValidationError,
        passwordRules,
        country,
    ]);
    function onSubmitForm() {
        setLoading(true);
        resetPass(values.newPassword, token, product, version)
            .then(() => setFlowState('success'))
            .catch(handleApiRequestError)
            .finally(() => {
            setLoading(false);
        });
    }
    const isFormSubmittable = values.newPassword && values.repeatNewPassword && isValid && meetsPasswordRequirements;
    if (flowState === 'success') {
        return (React.createElement(Container, null,
            React.createElement(ResetSuccess, null)));
    }
    if (flowState === 'failure') {
        return (React.createElement(Container, null,
            React.createElement(ResetFailure, null)));
    }
    // for the email to show up please add `&email=someemail@email.com` to the url
    return (React.createElement(React.Fragment, null,
        loading && React.createElement(LoadingModal, { "aria-label": "Loading" }),
        React.createElement(Container, null,
            React.createElement(Stack, { width: "100%", justifyContent: "center", alignItems: "center", gap: "$2" },
                React.createElement(Text, { type: "$h2.strong", testID: "ResetPassword.title", nativeID: "ResetPassword.title" }, i18n.t('ResetPassword.title')),
                React.createElement(Text, { textAlign: "center", testID: "ResetPassword.subtitle", nativeID: "ResetPassword.subtitle" }, i18n.t('ResetPassword.subtitle')),
                React.createElement(Text, { style: {
                        wordBreak: 'break-word',
                    }, textAlign: "center", testID: "ResetPassword.title-email", nativeID: "ResetPassword.title-email" }, email)),
            React.createElement(FormContainer, { hasHiddenUserInput: true },
                React.createElement(PasswordInputValidator, { placeholder: i18n.t('ResetPassword.content.resetPasswordForm.formField.newPassword.placeholder'), label: i18n.t('ResetPassword.content.resetPasswordForm.formField.newPassword.label'), onChangeText: handleChange('newPassword'), errors: errors, touched: touched, handleBlur: handleBlur('newPassword'), password: values.newPassword, onValidate: setMeetsPasswordRequirements, passwordRules: passwordRules, testID: "newPassword" }),
                React.createElement(InputPassword, { label: i18n.t('ResetPassword.content.resetPasswordForm.formField.repeatNewPassword.label'), placeholder: i18n.t('ResetPassword.content.resetPasswordForm.formField.repeatNewPassword.placeholder'), testID: "repeatNewPassword", onBlur: handleBlur('repeatNewPassword'), onChangeText: handleChange('repeatNewPassword'), onSubmitEditing: () => (isFormSubmittable ? handleSubmit() : {}), defaultValue: values.repeatNewPassword, isInvalid: errors.repeatNewPassword && touched.repeatNewPassword ? true : false, errorMessage: errors.repeatNewPassword })),
            React.createElement(Button, { testID: "ResetPassword.button", onPress: () => handleSubmit(), disabled: !isFormSubmittable, label: i18n.t('ResetPassword.title'), "aria-label": i18n.t('ResetPassword.title') }))));
};
export default connector(ResetPassword);
